import {
  DesignerParams,
  DraftState,
  GenericBasketItem,
  PrintType,
  Product,
  ProductType,
  SharingDraft,
} from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { fetchDraft } from '@/utils/http/fetchDraft';
import { fetchProduct } from '@/utils/http/fetchProduct';
import { fetchProductForSellable } from '@/utils/http/fetchProductForSellable';
import { fetchTemplate } from '@/utils/http/fetchTemplate';
import { addProductToHistory } from '@/utils/productHistory';
import { safeString } from '@/utils/safeString';
import { sanitizeText } from '@/utils/text/sanitizeText';
import { getDefaultColor } from '@/model/configuration/getDefaultColor';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getPrintTypeForFontOnDraft } from '@/model/draft/getPrintTypeForFontOnDraft';
import { mapDraftToSharingDraft } from '@/model/draftMapper/mapDraftToSharingDraft';
import { mapProductToSharingDraft } from '@/model/draftMapper/mapProductToSharingDraft';
import { getClosestPrintColor } from '@/model/printType/getClosestPrintColor';
import { getPrintColor } from '@/model/printType/getPrintColor';
import { isPrintColorColorSpace } from '@/model/printType/isPrintColorColorSpace';
import { printTypesForDesign } from '@/model/printType/printTypesForDesign';
import { getAppearance } from '@/model/productType/getAppearance';
import { getDefaultAppearanceId } from '@/model/productType/getDefaultAppearanceId';
import { getDefaultPrintAreaForView } from '@/model/productType/getDefaultPrintAreaForView';
import { getDefaultView } from '@/model/productType/getDefaultView';
import { getView } from '@/model/productType/getView';
import { getViewByPerspective } from '@/model/productType/getViewByPerspective';
import { getViewForPrintArea } from '@/model/productType/getViewForPrintArea';
import { loadBasketAction } from '@/redux/basket/actions/loadBasketAction';
import { getAllBasketItemsWithSameProductId } from '@/redux/basket/getters/getAllBasketItemsWithSameProductId';
import { ensureFontIsAddedToDocumentAction } from '@/redux/core/actions/ensureFontIsAddedToDocumentAction';
import { findFont } from '@/redux/core/getters/findFont';
import { getDefaultFont } from '@/redux/core/getters/getDefaultFont';
import { getFonts } from '@/redux/core/getters/getFonts';
import { fetchDesignAction } from '@/redux/designs/actions/fetchDesignAction';
import { loadFromStorage } from '@/redux/draft/memento/loadFromStorage';
import { getPrintTypes } from '@/redux/printTypes/getters/getPrintTypes';
import { fetchFirstProductTypeAction } from '@/redux/productTypes/actions/fetchFirstProductTypeAction';
import { fetchProductTypeAction } from '@/redux/productTypes/actions/fetchProductTypeAction';
import { StoreDispatch, StoreState } from '@/redux/types';
import { addDesignAction } from '@/components/draft/draftState/actions/addDesignAction';
import { addTextAction } from '@/components/draft/draftState/actions/addTextAction';
import { setDraftAction } from '@/components/draft/draftState/actions/setDraftAction';

const getViewFromProductTypeAndParams = (productType: ProductType, params: DesignerParams) =>
  getView(productType, String(params.viewId)) ||
  getViewByPerspective(productType, String(params.perspective)) ||
  getDefaultView(productType);

const getDraftForProduct = (product: Product, params: DesignerParams, state: StoreState) =>
  mapProductToSharingDraft(product, getPrintTypes(state), getFonts(state));

const getSizeMapForBasketItems = (basketItems: GenericBasketItem[]): Record<string, number> => {
  return basketItems.reduce<Record<string, number>>((result, basketItem) => {
    const size = basketItem.sizeId;
    if (size) {
      result[size] = basketItem.quantity;
    }
    return result;
  }, {});
};

export const bootstrapWithBasketItem = async (
  basketId: string,
  basketItemId: string,
  fallbackProductId: string,
  params: DesignerParams,
  dispatch: StoreDispatch,
  getState: () => StoreState
): Promise<[SharingDraft, DraftState['selectedSizes'], string?]> => {
  await dispatch(loadBasketAction(basketId));
  const items = getAllBasketItemsWithSameProductId(getState(), basketItemId);
  if (items.length) {
    const sizeMap = getSizeMapForBasketItems(items);
    const item = items[0];
    return [await bootstrapWithProduct(item.productId, params, getState), sizeMap, item.id];
  } else if (fallbackProductId) {
    // temporary fix for basket issue in MP
    return [await bootstrapWithProduct(fallbackProductId, params, getState), {}];
  }

  throw new Error(`Basket item unavailable`);
};
export const bootstrapWithProduct = async (
  productId: string,
  params: DesignerParams,
  getState: () => StoreState
) => {
  const product = await fetchProduct(productId);
  return getDraftForProduct(product, params, getState());
};

export const bootstrapWithSellable = async (
  sellableId: string,
  params: DesignerParams,
  getState: () => StoreState
) => {
  const product = await fetchProductForSellable(sellableId);
  return getDraftForProduct(product, params, getState());
};

export const bootstrapWithDraft = (draftId: string) => fetchDraft(draftId);

export const bootstrapWithTemplate = (templateId: string) => fetchTemplate(templateId);

export const bootstrapWithProductType = async (
  params: DesignerParams,
  dispatch: StoreDispatch,
  getState: () => StoreState
) => {
  const productTypeId = safeString(params.productTypeId);
  const designId = safeString(params.designId);
  const tx1 = safeString(params.tx1);

  const productType = await (productTypeId
    ? dispatch(fetchProductTypeAction(productTypeId))
    : dispatch(fetchFirstProductTypeAction));

  const appearance = assertDefined(
    getAppearance(productType, String(params.appearanceId)) || getAppearance(productType)
  );
  const view = getViewFromProductTypeAndParams(productType, params);

  const draft = {} as DraftState;
  setDraftAction({ productType, appearance, view })(draft);

  if (designId) {
    // it is not always possible to add the design on the default print area (e.g. pixel design on flex only)
    // so try to find the next possible print area and switch to it
    const design = await dispatch(fetchDesignAction(designId));
    const defaultPrintArea = assertDefined(getDefaultPrintAreaForView(productType, view));
    let printType: PrintType | undefined;

    const printTypes = printTypesForDesign(design, defaultPrintArea, appearance);

    if (printTypes.length > 0) {
      printType = printTypes[0];
    } else {
      for (const otherPrintArea of productType.printAreas) {
        const otherPrintTypes =
          !otherPrintArea.deprecated &&
          otherPrintArea !== defaultPrintArea &&
          printTypesForDesign(design, otherPrintArea, appearance);
        if (otherPrintTypes && otherPrintTypes.length > 0) {
          draft.view = getViewForPrintArea(productType, otherPrintArea);
          printType = otherPrintTypes[0];
          break;
        }
      }
    }

    if (printType) {
      addDesignAction(design)(draft);
    }
  }

  if (tx1) {
    const state = getState();

    const font =
      findFont(state, safeString(params.fontFamilyName), params.fontStyle, params.fontWeight) ||
      getDefaultFont(getFonts(state));

    const printType =
      getConfigurations(draft)[0]?.printType || getPrintTypeForFontOnDraft(draft, font);

    let color: string | undefined;
    if (params.textColor || params.textColorRgb) {
      if (isPrintColorColorSpace(printType)) {
        const printColor =
          (params.textColor && getPrintColor(printType, params.textColor)) ||
          (params.textColorRgb && getClosestPrintColor(printType, params.textColorRgb));
        if (printColor) {
          color = printColor.fill;
        }
      } else if (params.textColorRgb) {
        color = params.textColorRgb;
      }
    }

    if (!color) {
      color = getDefaultColor(appearance);
    }

    const loadedFont = await dispatch(ensureFontIsAddedToDocumentAction(font));
    addTextAction(sanitizeText(tx1), loadedFont, color)(draft);
  }

  return mapDraftToSharingDraft(draft);
};

export const bootstrapWithMemento = (params: DesignerParams) => {
  const sharingDraft = loadFromStorage(params.contextId, params.platform);

  if (sharingDraft) {
    if (sharingDraft.product) {
      addProductToHistory(sharingDraft.product, sharingDraft);
    }

    return sharingDraft;
  }
};

export const bootstrapWithEmptyProduct = async (
  params: DesignerParams,
  dispatch: StoreDispatch
): Promise<SharingDraft> => {
  const productType = await dispatch(fetchFirstProductTypeAction);
  const paramAppearanceId = safeString(params.appearanceId);
  const appearanceId =
    (paramAppearanceId && getAppearance(productType, paramAppearanceId)?.id) ||
    getDefaultAppearanceId(productType);
  const viewId = getViewFromProductTypeAndParams(productType, params).id;

  return {
    productTypeId: productType.id,
    appearanceId,
    viewId,
    configurations: [],
  };
};
